import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PrenatalIntakeFormData } from "../PrenatalIntakeFormSchema";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";

interface MentalHealthSectionProps {
  form: UseFormReturn<PrenatalIntakeFormData>;
}

export function MentalHealthSection({ form }: MentalHealthSectionProps) {
  const tobaccoBefore = form.watch("tobacco_before_pregnancy");
  const tobaccoDuring = form.watch("tobacco_during_pregnancy");
  const alcoholBefore = form.watch("alcohol_before_pregnancy");
  const alcoholDuring = form.watch("alcohol_during_pregnancy");
  const hasMentalHealthConcerns = form.watch("mental_health_concerns");

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold border-b pb-2">Mental Health and Substance Use</h3>

      <Alert variant="default" className="bg-muted">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          This information helps us provide better care. Your answers are confidential and will not affect your eligibility for services.
        </AlertDescription>
      </Alert>

      {/* Tobacco Use */}
      <div className="space-y-4">
        <h4 className="font-medium">Tobacco Use</h4>
        
        <FormField
          control={form.control}
          name="tobacco_before_pregnancy"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Used tobacco products before this pregnancy</FormLabel>
                <FormDescription>Including cigarettes or e-cigarettes. * Risk factor</FormDescription>
              </div>
            </FormItem>
          )}
        />

        {tobaccoBefore && (
          <FormField
            control={form.control}
            name="tobacco_before_pregnancy_notes"
            render={({ field }) => (
              <FormItem className="ml-6">
                <FormLabel>What tobacco products did you use?</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="e.g., cigarettes, e-cigarettes, etc." 
                    className="min-h-[60px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="tobacco_during_pregnancy"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Used tobacco products during this pregnancy</FormLabel>
                <FormDescription>* Risk factor for PNCC eligibility</FormDescription>
              </div>
            </FormItem>
          )}
        />

        {tobaccoDuring && (
          <FormField
            control={form.control}
            name="tobacco_during_pregnancy_notes"
            render={({ field }) => (
              <FormItem className="ml-6">
                <FormLabel>What tobacco products are you using?</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="e.g., cigarettes, e-cigarettes, etc." 
                    className="min-h-[60px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="household_tobacco_use"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Someone in my household smokes or uses tobacco products</FormLabel>
                <FormDescription>* Risk factor for PNCC eligibility</FormDescription>
              </div>
            </FormItem>
          )}
        />
      </div>

      {/* Alcohol Use */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium">Alcohol Use</h4>
        
        <FormField
          control={form.control}
          name="alcohol_before_pregnancy"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Drank alcohol in the three months before this pregnancy</FormLabel>
              </div>
            </FormItem>
          )}
        />

        {alcoholBefore && (
          <FormField
            control={form.control}
            name="alcohol_before_pregnancy_amount"
            render={({ field }) => (
              <FormItem className="ml-6">
                <FormLabel>About how many drinks per week?</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="e.g., occasional drinker, 2-3 drinks per week" 
                    className="min-h-[60px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="alcohol_during_pregnancy"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Drank alcohol during this pregnancy</FormLabel>
                <FormDescription>* Risk factor for PNCC eligibility</FormDescription>
              </div>
            </FormItem>
          )}
        />

        {alcoholDuring && (
          <FormField
            control={form.control}
            name="alcohol_during_pregnancy_amount"
            render={({ field }) => (
              <FormItem className="ml-6">
                <FormLabel>About how many drinks per week?</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="e.g., occasional, 1-2 drinks per week" 
                    className="min-h-[60px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>

      {/* Drug Use */}
      <div className="pt-4 border-t">
        <FormField
          control={form.control}
          name="non_prescribed_drug_use"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Used non-prescribed drugs in the past year</FormLabel>
                <FormDescription>Or used drugs in a way other than prescribed. * Risk factor</FormDescription>
              </div>
            </FormItem>
          )}
        />
      </div>

      {/* Mental Health */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium">Mental Health</h4>
        
        <FormField
          control={form.control}
          name="feeling_depressed"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Lost interest in doing things or felt down, depressed, or hopeless</FormLabel>
                <FormDescription>In the past month. * Risk factor for PNCC eligibility</FormDescription>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="stress_level"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Current stress level</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-row space-x-6"
                >
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="high" />
                    </FormControl>
                    <FormLabel className="font-normal">High *</FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="medium" />
                    </FormControl>
                    <FormLabel className="font-normal">Medium</FormLabel>
                  </FormItem>
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <RadioGroupItem value="low" />
                    </FormControl>
                    <FormLabel className="font-normal">Low</FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="mental_health_concerns"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Concerns about mental health or substance use</FormLabel>
              </div>
            </FormItem>
          )}
        />

        {hasMentalHealthConcerns && (
          <FormField
            control={form.control}
            name="mental_health_concerns_notes"
            render={({ field }) => (
              <FormItem className="ml-6">
                <FormLabel>Please describe your concerns (optional)</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Share any concerns you'd like us to know about..." 
                    className="min-h-[80px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}
